'use client';

import { createActorContext } from '@xstate/react';

import { getAuthMachine } from '@/machines';
import { logger } from '@/utils';

// LocalStorage key for persisting state
const AUTH_MACHINE_LOCAL_STORAGE_KEY = 'auth-machine-state';

// Load persisted state from localStorage
const loadPersistedState = () => {
  if (typeof window === 'undefined') return undefined;

  try {
    const persistedState = localStorage.getItem(AUTH_MACHINE_LOCAL_STORAGE_KEY);
    if (persistedState) {
      logger.debug('Loaded persisted auth state from localStorage');
      return JSON.parse(persistedState);
    }
    return undefined;
  } catch (e) {
    logger.error(`Failed to load persisted auth state: ${e}`);
    return undefined;
  }
};

const AuthMachine = getAuthMachine();

// Create context with the state machine
const AuthMachineContext = createActorContext(AuthMachine, {
  state: loadPersistedState(),
});

// Export the hooks directly from the context
const { useActorRef, useSelector } = AuthMachineContext;

// Provider component that handles persistence to localStorage
const AuthMachineProvider = ({ children }: { children: React.ReactNode }) => {
  return <AuthMachineContext.Provider>{children}</AuthMachineContext.Provider>;
};

export {
  AUTH_MACHINE_LOCAL_STORAGE_KEY,
  AuthMachineContext,
  AuthMachineProvider,
  useActorRef as useAuthActorRef,
  useSelector as useAuthSelector,
};
